"use client";
import { useMemo, useState } from "react";
import { sortBy } from "lodash";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/app/admin/_common/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/admin/_common/ui/popover";
import { Input } from "@/app/admin/_common/ui/input";
import { Label } from "@/app/admin/_common/ui/label";
import { Checkbox } from "@/app/admin/_common/ui/checkbox";
import CuoButton from "@/app/admin/_common/CuoButton";
import { formatUtcDate } from "@/app/_utils/dayjs";
import { ArrowLeftRight, ChevronDown, Search } from "lucide-react";
import { toast } from "@/app/_utils/toast";
import { getCsrfToken } from "@/app/_utils/csrf";

export default function SwapDialog({
  open,
  onClose,
  order,
  showId,
  futureShows = [],
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  order: any;
  showId?: string;
  futureShows?: any[];
  onSuccess?: () => void;
}) {
  const [targetShowId, setTargetShowId] = useState<string | undefined>();
  const [adjustTickets, setAdjustTickets] = useState(false);
  const [query, setQuery] = useState("");
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const baseShowId = showId ?? order?.attending_show ?? order?.show;
  const shows = useMemo(() => {
    const list = Array.isArray(futureShows) ? futureShows : [];
    return sortBy(
      list.filter((it: any) => String(it.id) !== String(baseShowId)),
      "start"
    );
  }, [futureShows, baseShowId]);

  if (!order) return null;

  const filteredShows = shows.filter(
    (s: any) => !query.trim() || s.title?.toLowerCase().includes(query.trim().toLowerCase())
  );
  const targetShow = shows.find((s: any) => String(s.id) === String(targetShowId));

  function handleClose() {
    setTargetShowId(undefined);
    setAdjustTickets(false);
    setQuery("");
    onClose();
  }

  async function handleSwap() {
    if (!targetShowId) {
      toast.warning("Select a show to swap to");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}show/order/`, {
        method: "PUT",
        credentials: "include",
        body: JSON.stringify({ action: "swap", orderId: order.id, targetShowId, adjustTickets }),
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCsrfToken(),
        },
      });
      if (res.status === 200) {
        toast.success("Guest swapped to new show");
        handleClose();
        if (onSuccess) onSuccess();
        else window.location.reload();
      } else {
        toast.error("An error has occurred");
      }
    } catch (e) {
      toast.error("An error has occurred");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !loading && handleClose()}>
      <DialogContent className="max-w-[440px]">
        <DialogHeader>
          <DialogTitle>
            <span className="inline-flex items-center gap-2 align-middle">
              <ArrowLeftRight className="size-4 flex-none text-muted-foreground" />
              Swap show
            </span>
          </DialogTitle>
          <DialogDescription>Move this guest to a different show.</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label>Select show</Label>
            <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex h-9 items-center justify-between rounded-md border border-input bg-card px-3 text-[13px] text-foreground hover:bg-muted"
                >
                  <span className={targetShow ? "text-foreground" : "text-muted-foreground"}>
                    {targetShow
                      ? targetShow.variant_name
                        ? `${targetShow.title}: ${targetShow.variant_name}`
                        : targetShow.title
                      : "Choose a show…"}
                  </span>
                  <ChevronDown className="size-3.5 text-muted-foreground" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-[360px] p-2">
                <div className="relative mb-2">
                  <div className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center">
                    <Search className="size-3.5 text-muted-foreground" />
                  </div>
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search shows…"
                    className="h-[30px] pl-8 text-[12.5px]"
                    autoFocus
                  />
                </div>
                <div className="max-h-[240px] overflow-y-auto">
                  {filteredShows.length === 0 ? (
                    <div className="px-2 py-3 text-center text-[12.5px] text-muted-foreground">
                      No available shows to swap
                    </div>
                  ) : (
                    filteredShows.map((s: any) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => {
                          setTargetShowId(String(s.id));
                          setPopoverOpen(false);
                          setQuery("");
                        }}
                        className="flex w-full flex-col items-start rounded-md px-2 py-1.5 text-left text-[12.5px] hover:bg-muted"
                      >
                        <span className="truncate text-foreground">
                          {s.variant_name ? `${s.title}: ${s.variant_name}` : s.title}
                        </span>
                        <span className="text-[11px] text-muted-foreground">{formatUtcDate(s.start)}</span>
                      </button>
                    ))
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <label className="flex items-center gap-2 text-[13px] text-foreground">
            <Checkbox checked={adjustTickets} onCheckedChange={(c) => setAdjustTickets(Boolean(c))} />
            Adjust tickets: this show +{order.quantity}, target show -{order.quantity}
          </label>
        </div>

        <DialogFooter>
          <CuoButton variant="outline" label="Cancel" onClick={handleClose} disabled={loading} />
          <CuoButton disableOnAsync={loading} variant="default" label="Swap" onClick={handleSwap} />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
